// CRUD de productos. El stock es independiente por bodega; stockTotal se agrega
// en cliente. Regla: no eliminar un producto usado como componente de un kit.
import { insforge } from '../insforge';
import { registrarMovimiento } from './movimientos';
import type { Producto } from '../../types';

export async function listarProductos(): Promise<Producto[]> {
  const [{ data: productos, error: e1 }, { data: inv, error: e2 }] = await Promise.all([
    insforge.database.from('productos').select('*').order('nombre'),
    insforge.database.from('inventario').select('ref_id, stock').eq('tipo', 'producto'),
  ]);
  if (e1) throw new Error(e1.message);
  if (e2) throw new Error(e2.message);

  const totales = new Map<string, number>();
  for (const r of (inv ?? []) as { ref_id: string; stock: number }[]) {
    totales.set(r.ref_id, (totales.get(r.ref_id) ?? 0) + Number(r.stock));
  }
  return ((productos ?? []) as Producto[]).map((p) => ({ ...p, stockTotal: totales.get(p.id) ?? 0 }));
}

export interface EntradaProducto {
  nombre: string;
  sku?: string;
  descripcion?: string;
  foto?: string;
  unidad?: string;
  stock_minimo?: number;
  estado?: Producto['estado'];
}

function normalizar(input: EntradaProducto) {
  return {
    nombre: input.nombre,
    sku: input.sku ?? '',
    descripcion: input.descripcion ?? '',
    foto: input.foto ?? '',
    unidad: input.unidad ?? 'Unidad',
    stock_minimo: Number(input.stock_minimo ?? 0),
    estado: input.estado ?? 'Activo',
  };
}

export async function crearProducto(input: EntradaProducto): Promise<Producto> {
  const { data, error } = await insforge.database.from('productos').insert([normalizar(input)]).select();
  if (error) throw new Error(error.message);
  const producto = (data as Producto[])[0];
  await registrarMovimiento({
    tipo: 'Producto creado',
    descripcion: 'Se creó el producto ' + producto.nombre,
    item_tipo: 'producto',
    item_id: producto.id,
    item_nombre: producto.nombre,
  });
  return producto;
}

export async function editarProducto(id: string, input: EntradaProducto): Promise<Producto> {
  const { data, error } = await insforge.database.from('productos').update(normalizar(input)).eq('id', id).select();
  if (error) throw new Error(error.message);
  const producto = (data as Producto[])[0];
  await registrarMovimiento({
    tipo: 'Producto editado',
    descripcion: 'Se editó el producto ' + producto.nombre,
    item_tipo: 'producto',
    item_id: id,
    item_nombre: producto.nombre,
  });
  return producto;
}

export async function eliminarProducto(id: string): Promise<void> {
  // Regla: no eliminar si es componente de alguna presentación.
  const { data: usado, error: e1 } = await insforge.database
    .from('detalle_presentaciones')
    .select('id')
    .eq('producto_id', id)
    .limit(1);
  if (e1) throw new Error(e1.message);
  if (usado && usado.length > 0) {
    throw new Error('El producto es componente de una presentación. Quítalo de la presentación antes de eliminarlo.');
  }

  const { data: prods } = await insforge.database.from('productos').select('nombre').eq('id', id).limit(1);
  const nombre = (prods as { nombre: string }[] | null)?.[0]?.nombre ?? id;

  // inventario.ref_id es polimórfico (sin FK): limpiar filas del producto a mano.
  const { error: e2 } = await insforge.database
    .from('inventario')
    .delete()
    .eq('tipo', 'producto')
    .eq('ref_id', id);
  if (e2) throw new Error(e2.message);

  const { error } = await insforge.database.from('productos').delete().eq('id', id);
  if (error) throw new Error(error.message);
  await registrarMovimiento({
    tipo: 'Producto eliminado',
    descripcion: 'Se eliminó el producto ' + nombre,
    item_tipo: 'producto',
    item_id: id,
  });
}
